import { Paper, Stack, Group, Text, Box, Progress, Badge, Tooltip } from '@mantine/core'
import { SectionHeader } from '../analysis/SectionHeader'
import { MarkdownRenderer } from '../common/MarkdownRenderer'

/**
 * Side-by-side comparison view for completed team-stories.
 *
 * Backend selects the mode based on team size — returned in the payload:
 *   - 'detailed'  — small teams: list every member's choice per decision point
 *   - 'aggregated' — large teams: distribution bars only, no per-member breakdown
 *
 * The frontend honors whatever the backend says; we don't second-guess.
 */
export function TeamComparisonView({ comparison, facilitatorView = false }) {
  const decisionPoints = comparison?.decision_points || []
  if (decisionPoints.length === 0) return null

  const mode = comparison.mode === 'detailed' ? 'detailed' : 'aggregated'

  return (
    <Box>
      <SectionHeader>How the Team Decided</SectionHeader>
      <Stack gap="md">
        {decisionPoints.map((dp) => (
          <Paper key={dp.beat_id} p="lg" className="glass">
            <Stack gap="sm">
              <Stack gap={2}>
                <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" fw={600} tt="uppercase" lts={1}>
                  {dp.beat_name || 'Decision Point'}
                </Text>
                {dp.prompt && (
                  <Box c="gray.4" style={{ fontSize: 14, lineHeight: 1.5 }}>
                    <MarkdownRenderer content={dp.prompt} />
                  </Box>
                )}
              </Stack>

              <Stack gap="sm">
                {(dp.choices || []).map((choice, i) => (
                  <ChoiceRow
                    key={i}
                    choice={choice}
                    showMembers={mode === 'detailed'}
                    facilitatorView={facilitatorView}
                  />
                ))}
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  )
}

function ChoiceRow({ choice, showMembers, facilitatorView }) {
  const pct = Math.round(choice.percentage || 0)
  return (
    <Box>
      <Group justify="space-between" mb={4} align="flex-start">
        <Text size="sm" c="cyan.2" fw={500} style={{ flex: 1 }}>
          {choice.choice_text}
        </Text>
        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
          {choice.count} ({pct}%)
        </Text>
      </Group>
      <Progress value={pct} color="cyan" size="sm" radius="xl" />
      {showMembers && choice.members?.length > 0 && (
        <Group gap={4} mt={6}>
          {choice.members.map((m, i) => {
            const tooltipLabel = facilitatorView && (m.full_name || m.email)
              ? `${m.full_name || ''}${m.full_name && m.email ? ' — ' : ''}${m.email || ''}`.trim()
              : null
            const badge = (
              <Badge size="xs" variant="light" color="cyan" styles={{ label: { textTransform: 'none' } }}>
                {m.display_name}
              </Badge>
            )
            return tooltipLabel
              ? <Tooltip key={i} label={tooltipLabel} withArrow><span>{badge}</span></Tooltip>
              : <span key={i}>{badge}</span>
          })}
        </Group>
      )}
    </Box>
  )
}
